import { tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { cssVariables, numberOrZero } from "@/utils/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SVK_VAT } from "../../../../../common/constants";
import { CommissionsOutput } from "../../types";

type Props = {
  data: CommissionsOutput;
};

type TableDataSource = {
  description: string;
  noVat: number;
  vatRate: number;
  vat: number;
  combined: number;
};

export const PaidCommissionDetailTable = ({ data }: Props) => {
  const { t } = useTranslation();

  const dataSource: TableDataSource[] = useMemo(() => {
    const result: TableDataSource[] = [];

    result.push({
      description: t("commissions.batch.helpers.subjectToVat"),
      noVat: numberOrZero(data.calculatedCommissionAmountWithoutVat),
      vatRate: SVK_VAT,
      vat: numberOrZero(data.calculatedCommissionAmountVat),
      combined:
        numberOrZero(data.calculatedCommissionAmountWithoutVat) + numberOrZero(data.calculatedCommissionAmountVat),
    });

    result.push({
      description: t("commissions.batch.helpers.exemptFromVat"),
      noVat: numberOrZero(data.calculatedCommissionAmountWithNoVat),
      vatRate: 0,
      vat: 0,
      combined: numberOrZero(data.calculatedCommissionAmountWithNoVat),
    });

    return result;
  }, []);

  const columns: ColumnsType<TableDataSource> = [
    {
      key: "description",
      width: 150,
      render: (_, record) => record.description,
    },
    {
      key: "noVat",
      title: t("commissions.batch.helpers.noVat"),
      align: "right",
      width: 125,
      render: (_, record) => formatLocaleCurrency(record.noVat),
    },
    {
      key: "vatRate",
      title: t("commissions.batch.helpers.vatRate"),
      align: "right",
      width: 125,
      render: (_, record) => formatLocalePercentageNumber(record.vatRate),
    },
    {
      key: "vat",
      title: t("common.vat"),
      align: "right",
      width: 100,
      render: (_, record) => formatLocaleCurrency(record.vat),
    },
    {
      key: "combined",
      title: t("commissions.batch.helpers.combinedAmount"),
      align: "right",
      width: 150,
      render: (_, record) => formatLocaleCurrency(record.combined),
    },
  ];

  return (
    <Table<TableDataSource>
      {...tableStandardProps()}
      scroll={{ x: 650 }}
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      summary={(pageData) => {
        let totalNoVat = 0;
        let totalVat = 0;
        let totalCombined = 0;

        pageData.forEach((record) => {
          totalNoVat += record.noVat;
          totalVat += record.vat;
          totalCombined += record.combined;
        });

        return (
          <Table.Summary.Row style={{ backgroundColor: cssVariables.colorHover }}>
            <Table.Summary.Cell index={0}>
              <b>{t("common.together")}</b>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              {formatLocaleCurrency(totalNoVat)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2}></Table.Summary.Cell>
            <Table.Summary.Cell index={3} align="right">
              {formatLocaleCurrency(totalVat)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={4} align="right">
              {formatLocaleCurrency(totalCombined)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        );
      }}
    />
  );
};
